import { useState, useEffect, useRef } from "react";
import { T, mono } from "./utils";
import { supabase } from "./supabase";

const WEEKLY_GOAL_CEVIK = 15;
const WEEKLY_GOAL_ADA = 15;
const MONTHS_TR = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
const CAL_BLACKLIST = ["fol films meeting","türkiye","tatil","holiday","birthday","doğum","contacts"];
const GCAL_COLORS = {"1":"#7986cb","2":"#33b679","3":"#8e24aa","4":"#e67c73","5":"#f6c026","6":"#f5511d","7":"#039be5","8":"#616161","9":"#3f51b5","10":"#0b8043","11":"#d60000"};

function getWeekStart(date) { const d=new Date(date); d.setDate(d.getDate()-((d.getDay()+6)%7)); d.setHours(0,0,0,0); return d; }
function addDays(date,n) { const d=new Date(date); d.setDate(d.getDate()+n); return d; }
function toDateStr(date) { return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`; }

async function fetchWeekEvents(googleToken, weekStart) {
  if (!googleToken) return [];
  try {
    const timeMin=new Date(weekStart).toISOString();
    const timeMax=addDays(weekStart,7).toISOString();
    const calRes=await fetch("https://www.googleapis.com/calendar/v3/users/me/calendarList",{headers:{Authorization:`Bearer ${googleToken}`}});
    const calData=await calRes.json();
    if(calData.error) return [];
    const calendars=(calData.items||[]).filter(c=>!CAL_BLACKLIST.some(b=>(c.summary||"").toLowerCase().includes(b)));
    const all=await Promise.all(calendars.map(async cal=>{
      try{
        const r=await fetch(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(cal.id)}/events?timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(timeMax)}&singleEvents=true&orderBy=startTime&maxResults=50`,{headers:{Authorization:`Bearer ${googleToken}`}});
        const d=await r.json();
        if(d.error) return [];
        return (d.items||[]).map(e=>({
          id:e.id+cal.id,
          title:e.summary||"(başlıksız)",
          date:e.start?.date||e.start?.dateTime?.slice(0,10),
          color:e.colorId?GCAL_COLORS[e.colorId]:(cal.backgroundColor||"#4a6fa5"),
          calendarName:cal.summary,
        }));
      }catch{return [];}
    }));
    return all.flat();
  }catch{return [];}
}

function EditableSummaryText({ text, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(text);
  const taRef = useRef();
  useEffect(()=>setVal(text),[text]);
  useEffect(()=>{
    if(editing && taRef.current){
      taRef.current.style.height="auto";
      taRef.current.style.height=taRef.current.scrollHeight+"px";
      taRef.current.focus();
      taRef.current.setSelectionRange(taRef.current.value.length,taRef.current.value.length);
    }
  },[editing]);
  const commit = () => { setEditing(false); if(val.trim()&&val.trim()!==text) onSave(val.trim()); };
  const autoGrow=(e)=>{ e.target.style.height="auto"; e.target.style.height=e.target.scrollHeight+"px"; };
  if (editing) return (
    <textarea ref={taRef} value={val}
      onChange={e=>{setVal(e.target.value);autoGrow(e);}}
      onBlur={commit} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();commit();}if(e.key==="Escape"){setVal(text);setEditing(false);}}}
      rows={1}
      style={{flex:1,fontSize:12,border:`1px solid ${T.accent}`,borderRadius:2,padding:"1px 6px",fontFamily:"'Inter',sans-serif",color:T.ink,background:"#fff",outline:"none",width:"100%",minWidth:0,boxSizing:"border-box",resize:"none",overflow:"hidden",lineHeight:1.4}}/>
  );
  return (
    <span onDoubleClick={()=>setEditing(true)} title="Düzenlemek için çift tıkla"
      style={{flex:1,fontSize:12,color:T.ink,lineHeight:1.3,cursor:"text",minWidth:0,overflowWrap:"break-word",wordBreak:"break-word"}}>
      {text}
    </span>
  );
}

function MinuteInput({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(()=>setVal(value),[value]);
  const commit = () => { setEditing(false); onChange(val); };
  if (editing) return (
    <input type="number" value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape")setEditing(false);}}
      autoFocus step="5" min="0" max="1440"
      style={{width:52,background:"#fff",border:`1px solid ${T.accent}`,borderRadius:2,padding:"2px 6px",...mono,fontSize:10,textAlign:"center",outline:"none",fontFamily:"'JetBrains Mono',monospace"}}/>
  );
  return (
    <span onClick={()=>setEditing(true)} title="Dakika gir"
      style={{...mono,fontSize:10,color:value?T.accent:T.border,cursor:"pointer",minWidth:44,textAlign:"right",borderBottom:`1px dashed ${value?T.accent:T.border}`}}>
      {value?`${value}dk`:"+ dk"}
    </span>
  );
}

export function SummaryView({ googleToken, accessLevel }) {
  const canSeeCevik = accessLevel==="full";
  const now = new Date();
  const todayStr = toDateStr(now);

  const [weekStart, setWeekStart] = useState(getWeekStart(now));
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [hours, setHours] = useState({});
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");

  const weekEnd = addDays(weekStart, 6);
  const weekLabel = weekStart.getMonth()===weekEnd.getMonth()
    ? `${weekStart.getDate()}\u2013${weekEnd.getDate()} ${MONTHS_TR[weekStart.getMonth()]} ${weekStart.getFullYear()}`
    : `${weekStart.getDate()} ${MONTHS_TR[weekStart.getMonth()]} \u2013 ${weekEnd.getDate()} ${MONTHS_TR[weekEnd.getMonth()]}`;

  const prevWeek=()=>setWeekStart(d=>addDays(d,-7));
  const nextWeek=()=>setWeekStart(d=>addDays(d,7));
  const goToday =()=>setWeekStart(getWeekStart(now));

  useEffect(()=>{ loadData(); },[weekStart]); // eslint-disable-line

  async function loadData() {
    setLoading(true);
    const weekStartStr = toDateStr(weekStart);
    const weekEndStr = toDateStr(weekEnd);
    const {data:projects}=await supabase.from("projects").select("id,title,kind,tasks(id,text,done,due_date,assignee,assignee2,dilsad)");
    const {data:etcTasks}=await supabase.from("tasks").select("id,text,done,due_date,assignee,dilsad,personal").eq("etc",true).not("due_date","is",null);
    const doneTasks=[];
    (projects||[]).forEach(p=>(p.tasks||[]).forEach(t=>{
      const effectiveDone = t.done;
      const inWeek = t.due_date && t.due_date >= weekStartStr && t.due_date <= weekEndStr;
      if (inWeek && effectiveDone) doneTasks.push({id:t.id,text:t.text,filmTitle:p.title,filmKind:p.kind,dueDate:t.due_date,assignee:t.assignee||"CEVIK",assignee2:t.assignee2||"",dilsad:!!t.dilsad});
    }));
    // Add etc=true tasks (project_id=null)
    (etcTasks||[]).forEach(t=>{
      if (t.personal && !canSeeCevik) return; // Personal görevler sadece Fol'a görünür
      const effectiveDone = t.done;
      const inWeek = t.due_date && t.due_date >= weekStartStr && t.due_date <= weekEndStr;
      if (inWeek && effectiveDone && !doneTasks.find(x=>x.id===t.id))
        doneTasks.push({id:t.id,text:t.text,filmTitle:t.personal?"Personal":"ETC.",filmKind:"",dueDate:t.due_date,assignee:t.assignee||"CEVIK",dilsad:!!t.dilsad});
    });
    setTasks(doneTasks);
    if (googleToken) {
      const evs = await fetchWeekEvents(googleToken, weekStart);
      setEvents(evs.filter(e=>e.date && e.date < todayStr));
    }
    const { data: weekData } = await supabase.from("summary_weeks").select("hours,note").eq("week_start", toDateStr(weekStart)).maybeSingle();
    const loadedHours = weekData?.hours || {};
    // Eski sürümden kalan sıfır/gecersiz girdileri temizle (hayalet toplam onarimi)
    const cleanedHours = {};
    for (const [k, v] of Object.entries(loadedHours)) {
      const num = Number(v);
      if (num > 0) cleanedHours[k] = num;
    }
    setHours(cleanedHours);
    setNote(weekData?.note || "");
    // Temizlik bir sey degistirdiyse DB'ye geri yaz
    if (Object.keys(cleanedHours).length !== Object.keys(loadedHours).length) {
      upsertWeek(cleanedHours, weekData?.note || "");
    }
    setLoading(false);
  }

  async function upsertWeek(newHours, newNote) {
    await supabase.from("summary_weeks").upsert({ week_start: toDateStr(weekStart), hours: newHours, note: newNote }, { onConflict: "week_start" });
  }

  function setHour(key, val) {
    const n = parseFloat(val);
    const newHours = { ...hours };
    if (val === "" || val === null || isNaN(n) || n <= 0) {
      delete newHours[key];          // silince key tamamen kalksin (0 birakma)
    } else {
      newHours[key] = Math.max(0, n);
    }
    setHours(newHours);
    upsertWeek(newHours, note);
  }

  function saveNote(v) {
    setNote(v);
    upsertWeek(hours, v);
  }

  async function toggleDilsad(taskId, current) {
    setTasks(p=>p.map(t=>t.id===taskId?{...t,dilsad:!current}:t));
    await supabase.from("tasks").update({dilsad:!current}).eq("id",taskId);
  }

  async function renameTask(taskId, newText) {
    if(!newText.trim()) return;
    setTasks(p=>p.map(t=>t.id===taskId?{...t,text:newText.trim()}:t));
    await supabase.from("tasks").update({text:newText.trim()}).eq("id",taskId);
  }

  async function toggleAssignee2(taskId, current, primary) {
    // If +CEVIK exists, remove it. If not, add it (but don't add if primary is already CEVIK)
    const next = current ? "" : (primary === "CEVIK" ? "ADA" : "CEVIK");
    setTasks(p=>p.map(t=>t.id===taskId?{...t,assignee2:next}:t));
    await supabase.from("tasks").update({assignee2:next}).eq("id",taskId);
  }

  const [assigneeFilter, setAssigneeFilter] = useState("ADA"); // "CEVIK" | "ADA"
  useEffect(()=>{ if(!canSeeCevik && assigneeFilter!=="ADA") setAssigneeFilter("ADA"); },[canSeeCevik, assigneeFilter]);
  const [showPersonal, setShowPersonal] = useState(false); // CEVIK: Personal isleri toplama dahil et

  const byProject = {};
  tasks.filter(t=>assigneeFilter==="all"||(t.assignee===assigneeFilter||t.assignee2===assigneeFilter)).filter(t=>showPersonal||t.filmTitle!=="Personal").forEach(t => {
    if (!byProject[t.filmTitle]) byProject[t.filmTitle] = { tasks:[], kind:t.filmKind };
    byProject[t.filmTitle].tasks.push(t);
  });

  const byCalendar = {};
  events.forEach(e => {
    const cal = e.calendarName || "Takvim";
    if (!byCalendar[cal]) byCalendar[cal] = [];
    byCalendar[cal].push(e);
  });

  const cevikMinutes = tasks.filter(t=>t.assignee==="CEVIK"||!t.assignee).reduce((s,t)=>s+(hours[t.id]||0),0);
  const adaMinutes = tasks.filter(t=>t.assignee==="ADA").reduce((s,t)=>s+(hours[t.id]||0),0);
  const dilsadMinutes = tasks.filter(t=>(t.assignee===assigneeFilter||t.assignee2===assigneeFilter)&&t.dilsad).reduce((s,t)=>s+(hours[t.id]||0),0);
  const personalMinutes = tasks.filter(t=>(t.assignee===assigneeFilter||t.assignee2===assigneeFilter)&&t.filmTitle==="Personal").reduce((s,t)=>s+(hours[t.id]||0),0);
  const hasPersonal = tasks.some(t=>(t.assignee===assigneeFilter||t.assignee2===assigneeFilter)&&t.filmTitle==="Personal");
  const totalHours = Object.values(hours).reduce((s,v)=>s+(v||0), 0) / 60;
  const WEEKLY_GOAL = assigneeFilter==="ADA" ? WEEKLY_GOAL_ADA : WEEKLY_GOAL_CEVIK;
  const totalForFilter = assigneeFilter==="ADA" ? adaMinutes : cevikMinutes;
  const shownMinutes = (assigneeFilter==="CEVIK" && !showPersonal) ? (totalForFilter - personalMinutes) : totalForFilter;
  const shownHours = shownMinutes / 60;
  const pct = Math.min((shownHours/WEEKLY_GOAL)*100, 100);

  const projectHours = {};
  // byProject ile ayni filtre: baslik toplami sadece gorunen/duzenlenebilir gorevleri yansitsin
  tasks.filter(t=>assigneeFilter==="all"||(t.assignee===assigneeFilter||t.assignee2===assigneeFilter)).filter(t=>showPersonal||t.filmTitle!=="Personal").forEach(t => { const h=hours[t.id]||0; projectHours[t.filmTitle]=(projectHours[t.filmTitle]||0)+h/60; });
  events.forEach(e => { const h=hours[e.id]||0; if(h>0) projectHours[e.calendarName||"Takvim"]=(projectHours[e.calendarName||"Takvim"]||0)+h/60; });

  const barColor = pct>=100?"#2d7a2d":pct>=70?"#c9843a":"#4a6fa5";

  if (loading) return <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{...mono,color:T.mute}}>Loading</span></div>;

  return (
    <div style={{flex:1,overflowY:"auto",padding:"32px 40px"}}>
      <div style={{maxWidth:900,margin:"0 auto"}}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:28}}>
          <div>
            <div style={{...mono,fontSize:9,color:T.mute,marginBottom:4,letterSpacing:"0.15em"}}>HAFTALIK ÖZET</div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <button onClick={prevWeek} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"3px 8px",cursor:"pointer",fontSize:12,color:T.mute}}>←</button>
              <span style={{fontSize:20,fontWeight:600,letterSpacing:"-0.02em"}}>{weekLabel}</span>
              <button onClick={nextWeek} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"3px 8px",cursor:"pointer",fontSize:12,color:T.mute}}>→</button>
              <button onClick={goToday} style={{...mono,fontSize:9,background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"3px 8px",cursor:"pointer",color:T.mute,textTransform:"uppercase"}}>Bugün</button>
            </div>
            {/* Assignee filter */}
            <div style={{display:"flex",gap:6,marginTop:10}}>
              {(canSeeCevik?["ADA","CEVIK"]:["ADA"]).map(a=>(
                <button key={a} onClick={()=>setAssigneeFilter(a)}
                  style={{...mono,fontSize:8,background:assigneeFilter===a?(a==="ADA"?"#9b7ec8":"#4a6fa5"):"none",color:assigneeFilter===a?"#fff":T.mute,border:`1px solid ${assigneeFilter===a?(a==="ADA"?"#9b7ec8":"#4a6fa5"):T.border}`,borderRadius:3,padding:"3px 8px",cursor:"pointer",fontWeight:600}}>
                  {a}
                </button>
              ))}
              {assigneeFilter==="CEVIK"&&hasPersonal&&(
                <button onClick={()=>setShowPersonal(s=>!s)}
                  style={{...mono,fontSize:8,background:showPersonal?"#e4572e":"none",color:showPersonal?"#fff":"#e4572e",border:"1px solid #e4572e",borderRadius:3,padding:"3px 8px",cursor:"pointer",fontWeight:600}}>
                  {showPersonal?"Personal dahil":`+ Personal (${Math.round(personalMinutes)}dk)`}
                </button>
              )}
            </div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:32,fontWeight:600,letterSpacing:"-0.03em",color:barColor,lineHeight:1}}>{Math.round(shownMinutes)}<span style={{fontSize:16,fontWeight:400,marginLeft:4}}>dk</span></div>
            <div style={{...mono,fontSize:9,color:T.mute}}>{shownHours.toFixed(1)} saat &nbsp;·&nbsp; {WEEKLY_GOAL} saat / {WEEKLY_GOAL*60} dk hedef</div>
            {/* Per-assignee breakdown */}
            <div style={{display:"flex",gap:10,marginTop:6,justifyContent:"flex-end"}}>
              {canSeeCevik&&<span style={{...mono,fontSize:9,color:"#4a6fa5"}}>CEVİK: <strong>{cevikMinutes}dk</strong></span>}
              <span style={{...mono,fontSize:9,color:"#9b7ec8"}}>ADA: <strong>{adaMinutes}dk</strong></span>
            </div>
          </div>
        </div>

        <div style={{marginBottom:28}}>
          <div style={{height:10,background:T.bg,borderRadius:5,overflow:"hidden",marginBottom:6,border:`1px solid ${T.border}`}}>
            <div style={{height:"100%",width:pct+"%",background:barColor,borderRadius:5,transition:"width .4s",position:"relative",overflow:"hidden"}}>
              {dilsadMinutes>0&&shownMinutes>0&&(
                <div title={`Dilşad: ${Math.round(dilsadMinutes)}dk`} style={{position:"absolute",top:0,left:0,height:"100%",width:`${Math.min((dilsadMinutes/shownMinutes)*100,100)}%`,background:"repeating-linear-gradient(45deg, rgba(0,0,0,0.4) 0, rgba(0,0,0,0.4) 3px, transparent 3px, transparent 6px)"}}/>
              )}
              {showPersonal&&personalMinutes>0&&shownMinutes>0&&(
                <div title={`Personal: ${Math.round(personalMinutes)}dk`} style={{position:"absolute",top:0,right:0,height:"100%",width:`${Math.min((personalMinutes/shownMinutes)*100,100)}%`,background:"repeating-linear-gradient(45deg, rgba(228,87,46,0.45) 0, rgba(228,87,46,0.45) 3px, transparent 3px, transparent 6px)"}}/>
              )}
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <span style={{...mono,fontSize:9,color:T.mute}}>{Math.round(pct)}% tamamlandi</span>
            <span style={{...mono,fontSize:9,color:pct>=100?"#2d7a2d":T.mute}}>
              {pct>=100?`Hedef aşıldı! +${Math.round((shownHours-WEEKLY_GOAL)*60)}dk`:`${Math.round((WEEKLY_GOAL-shownHours)*60)} dk kaldı`}
            </span>
          </div>
          {dilsadMinutes>0&&(
            <div style={{...mono,fontSize:9,color:T.mute,marginTop:6,display:"flex",alignItems:"center",gap:6}}>
              <span style={{display:"inline-block",width:9,height:9,borderRadius:2,background:"repeating-linear-gradient(45deg, rgba(0,0,0,0.4) 0, rgba(0,0,0,0.4) 2px, transparent 2px, transparent 4px)",border:`1px solid ${T.border}`}}/>
              <span>Dilşad'ın projeleri için <strong style={{color:T.ink}}>{Math.round(dilsadMinutes)}dk</strong></span>
              <span style={{color:T.border}}>·</span>
              <span>Toplam çalışma için <strong style={{color:T.ink}}>{Math.round(shownMinutes)}dk</strong></span>
            </div>
          )}
          {showPersonal&&personalMinutes>0&&(
            <div style={{...mono,fontSize:9,color:T.mute,marginTop:6,display:"flex",alignItems:"center",gap:6}}>
              <span style={{display:"inline-block",width:9,height:9,borderRadius:2,background:"repeating-linear-gradient(45deg, rgba(228,87,46,0.45) 0, rgba(228,87,46,0.45) 2px, transparent 2px, transparent 4px)",border:`1px solid ${T.border}`}}/>
              <span>Personal işler için <strong style={{color:"#e4572e"}}>{Math.round(personalMinutes)}dk</strong></span>
              <span style={{color:T.border}}>·</span>
              <span>Toplam çalışma için <strong style={{color:T.ink}}>{Math.round(shownMinutes)}dk</strong></span>
            </div>
          )}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) minmax(0,280px)",gap:28}}>
          <div>
            <div style={{marginBottom:20}}>
              <div style={{...mono,fontSize:9,color:T.mute,marginBottom:6}}>HAFTA NOTU</div>
              <textarea value={note} onChange={e=>saveNote(e.target.value)} placeholder="Bu haftaya dair notlar..."
                style={{width:"100%",height:64,background:"#fff",border:`1px solid ${T.border}`,borderRadius:2,padding:"8px 10px",fontSize:12,fontFamily:"'Inter',sans-serif",resize:"vertical",lineHeight:1.6,color:T.ink,outline:"none"}}/>
            </div>

            {Object.keys(byProject).length===0&&events.length===0&&(
              <div style={{...mono,fontSize:10,color:T.mute,padding:"24px 0"}}>Bu hafta icin tamamlanan gorev veya etkinlik yok.</div>
            )}

            {Object.entries(byProject).map(([filmTitle, {tasks:pts}])=>(
              <div key={filmTitle} style={{marginBottom:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8,paddingBottom:6,borderBottom:`1px solid ${T.border}`}}>
                  <span style={{fontSize:14,fontWeight:600,letterSpacing:"-0.01em"}}>{filmTitle}</span>
                  {projectHours[filmTitle]>0&&<span style={{...mono,fontSize:10,color:"#4a6fa5",fontWeight:600}}>{Math.round(projectHours[filmTitle]*60)}dk</span>}
                </div>
                {pts.map(t=>(
                  <div key={t.id} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"7px 0",borderBottom:`1px solid ${T.borderLight}`,flexWrap:"nowrap"}}>
                    <span style={{width:5,height:5,borderRadius:"50%",background:"#2d7a2d",flexShrink:0,marginTop:5}}/>
                    <div style={{flex:1,minWidth:0,display:"flex",flexWrap:"wrap",alignItems:"center",gap:4}}>
                      <EditableSummaryText text={t.text} onSave={v=>renameTask(t.id,v)}/>
                      <div style={{display:"flex",gap:4,alignItems:"center",flexShrink:0,flexWrap:"wrap"}}>
                        {t.assignee&&<span style={{...mono,fontSize:7,color:t.assignee==="ADA"?"#9b7ec8":"#4a6fa5",border:`1px solid ${t.assignee==="ADA"?"#9b7ec8":"#4a6fa5"}`,borderRadius:3,padding:"1px 4px"}}>{t.assignee}</span>}
                        <span
                          onClick={()=>toggleAssignee2(t.id,!!t.assignee2,t.assignee)}
                          title={t.assignee2?"Kaldırmak için tıkla":"Eklemek için tıkla"}
                          style={{...mono,fontSize:7,cursor:"pointer",borderRadius:3,padding:"1px 4px",
                            color: t.assignee2 ? (t.assignee2==="ADA"?"#9b7ec8":"#4a6fa5") : T.border,
                            border: `1px solid ${t.assignee2 ? (t.assignee2==="ADA"?"#9b7ec8":"#4a6fa5") : T.border}`,
                            opacity: t.assignee2 ? 1 : 0.4,
                          }}>
                          +{t.assignee2 || (t.assignee==="CEVIK"?"ADA":"CEVIK")}
                        </span>
                        <span onClick={()=>toggleDilsad(t.id,t.dilsad)} title="Dilşad için" style={{...mono,fontSize:7,fontWeight:600,cursor:"pointer",color:t.dilsad?"#fff":T.mute,background:t.dilsad?T.ink:"none",border:`1px solid ${t.dilsad?T.ink:T.border}`,borderRadius:3,padding:"1px 4px"}}>D</span>
                        {t.dueDate&&<span style={{...mono,fontSize:8,color:T.mute}}>{t.dueDate.slice(5)}</span>}
                        <MinuteInput value={hours[t.id]||""} onChange={v=>setHour(t.id,v)}/>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {Object.entries(byCalendar).map(([calName, evs])=>(
              <div key={calName} style={{marginBottom:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8,paddingBottom:6,borderBottom:`1px solid ${T.border}`}}>
                  <span style={{fontSize:14,fontWeight:600,letterSpacing:"-0.01em",color:"#4a6fa5"}}>{calName}</span>
                  {projectHours[calName]>0&&<span style={{...mono,fontSize:10,color:"#4a6fa5",fontWeight:600}}>{Math.round(projectHours[calName]*60)}dk</span>}
                </div>
                {evs.map(e=>(
                  <div key={e.id} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"7px 0",borderBottom:`1px solid ${T.borderLight}`}}>
                    <span style={{width:5,height:5,borderRadius:"50%",background:e.color,flexShrink:0,marginTop:5}}/>
                    <div style={{flex:1,minWidth:0,display:"flex",flexWrap:"wrap",alignItems:"center",gap:4}}>
                      <span style={{flex:1,minWidth:0,fontSize:12,color:T.ink,lineHeight:1.3,wordBreak:"break-word"}}>{e.title}</span>
                      <div style={{display:"flex",gap:4,flexShrink:0}}>
                        {e.date&&<span style={{...mono,fontSize:8,color:T.mute}}>{e.date.slice(5)}</span>}
                        <MinuteInput value={hours[e.id]||""} onChange={v=>setHour(e.id,v)}/>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div>
            <div style={{...mono,fontSize:9,color:T.mute,marginBottom:10}}>PROJE BAZLI SURELER</div>
            {Object.keys(projectHours).length===0?(
              <div style={{...mono,fontSize:9,color:T.mute}}>Henuz sure girilmemis.</div>
            ):(
              <div style={{border:`1px solid ${T.border}`,borderRadius:2,overflow:"hidden"}}>
                {Object.entries(projectHours).sort((a,b)=>b[1]-a[1]).map(([proj,h],i,arr)=>(
                  <div key={proj} style={{padding:"10px 14px",background:i%2===0?"#fff":"#f7f7f6",borderBottom:i<arr.length-1?`1px solid ${T.borderLight}`:"none"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                      <span style={{fontSize:12,fontWeight:500,color:T.ink}}>{proj}</span>
                      <span style={{...mono,fontSize:11,fontWeight:600,color:"#4a6fa5"}}>{Math.round(h*60)}dk</span>
                    </div>
                    <div style={{height:3,background:T.bg,borderRadius:2,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${Math.min((h/WEEKLY_GOAL)*100,100)}%`,background:"#4a6fa5",borderRadius:2}}/>
                    </div>
                  </div>
                ))}
                <div style={{padding:"10px 14px",background:"#f7f7f6",display:"flex",justifyContent:"space-between",borderTop:`1px solid ${T.border}`}}>
                  <span style={{...mono,fontSize:9,color:T.mute}}>TOPLAM</span>
                  <span style={{...mono,fontSize:11,fontWeight:600,color:T.ink}}>{Math.round(shownMinutes)}dk</span>
                </div>
              </div>
            )}

            <div style={{marginTop:20,...mono,fontSize:9,color:T.mute,marginBottom:8}}>HAFTA ISTATISTIKLERI</div>
            <div style={{border:`1px solid ${T.border}`,borderRadius:2,overflow:"hidden"}}>
              {[
                {l:"Tamamlanan Gorev",v:tasks.length},
                {l:"Takvim Etkinligi",v:events.length},
                {l:"Aktif Proje",v:Object.keys(byProject).length},
              ].map((s,i)=>(
                <div key={s.l} style={{display:"flex",justifyContent:"space-between",padding:"9px 14px",background:i%2===0?"#fff":"#f7f7f6",borderBottom:i<2?`1px solid ${T.borderLight}`:"none"}}>
                  <span style={{fontSize:12,color:T.mute}}>{s.l}</span>
                  <span style={{...mono,fontSize:11,fontWeight:600,color:T.ink}}>{s.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
